import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, FileText, TrendingUp, TrendingDown, AlertCircle, Calendar, Download, BarChart3, GitCompareArrows } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Accounting = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [compareYear, setCompareYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAvailableYears(); }, []);
  useEffect(() => { if (selectedYear) fetchYearlyData(selectedYear, false); }, [selectedYear]);
  useEffect(() => {
    if (compareYear) fetchYearlyData(compareYear, true);
    else setCompareData(null);
  }, [compareYear]);

  const fetchAvailableYears = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/available-years`);
      setAvailableYears(res.data.years);
      if (res.data.years.length > 0) setSelectedYear(res.data.years[0]);
    } catch (e) { console.error(e); }
  };

  const fetchYearlyData = async (year, isCompare) => {
    if (!isCompare) setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/yearly-summary/${year}`);
      if (isCompare) setCompareData(res.data);
      else setData(res.data);
    } catch (e) {
      toast.error('Failed to load accounting data');
    } finally {
      if (!isCompare) setLoading(false);
    }
  };

  const formatCurrency = (a) => `$${(a || 0).toFixed(2)}`;

  // Calculate year-over-year % delta
  const calcDelta = (curr, prev) => {
    const c = curr || 0;
    const p = prev || 0;
    if (p === 0 && c === 0) return { pct: 0, dir: 'flat' };
    if (p === 0) return { pct: 100, dir: 'up' };
    const pct = ((c - p) / Math.abs(p)) * 100;
    let dir = 'flat';
    if (pct > 0.5) dir = 'up';
    else if (pct < -0.5) dir = 'down';
    return { pct, dir };
  };

  const DeltaBadge = ({ delta, invertColor = false }) => {
    if (!delta) return null;
    const isGood = invertColor ? delta.dir === 'down' : delta.dir === 'up';
    let color = 'text-gray-500';
    if (delta.dir !== 'flat') color = isGood ? 'text-green-600' : 'text-red-600';
    let Icon = null;
    if (delta.dir === 'up') Icon = TrendingUp;
    else if (delta.dir === 'down') Icon = TrendingDown;
    return (
      <span className={`text-xs font-semibold ${color} flex items-center gap-1`}>
        {Icon && <Icon size={12} />} {delta.pct >= 0 ? '+' : ''}{delta.pct.toFixed(1)}%
      </span>
    );
  };


  const exportToCSV = () => {
    if (!data) return;
    const headers = ['Month', 'Invoices', 'Vehicle Revenue', 'Warehouse Revenue', 'Total Revenue', 'Supplier Cost', 'Profit', 'Paid', 'Outstanding'];
    const rows = data.monthly_breakdown.map(m => [
      m.month_name, m.invoice_count,
      m.vehicle_revenue.toFixed(2), m.warehouse_revenue.toFixed(2),
      m.revenue.toFixed(2), m.supplier_cost.toFixed(2), m.profit.toFixed(2),
      m.paid.toFixed(2), m.outstanding.toFixed(2),
    ]);
    rows.push(['TOTAL', data.summary.total_invoices,
      data.monthly_breakdown.reduce((s, m) => s + m.vehicle_revenue, 0).toFixed(2),
      data.monthly_breakdown.reduce((s, m) => s + m.warehouse_revenue, 0).toFixed(2),
      data.summary.total_revenue.toFixed(2),
      data.summary.total_supplier_cost.toFixed(2),
      data.summary.total_profit.toFixed(2),
      data.summary.paid_amount.toFixed(2),
      data.summary.outstanding_amount.toFixed(2),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `accounting_${selectedYear}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const chartData = data ? data.monthly_breakdown.map((m, i) => {
    const cm = compareData ? compareData.monthly_breakdown[i] : null;
    const row = {
      month: m.month_short,
      [`Revenue ${selectedYear}`]: parseFloat(m.revenue.toFixed(2)),
      [`Cost ${selectedYear}`]: parseFloat(m.supplier_cost.toFixed(2)),
      [`Profit ${selectedYear}`]: parseFloat(m.profit.toFixed(2)),
    };
    if (cm) {
      row[`Revenue ${compareYear}`] = parseFloat(cm.revenue.toFixed(2));
      row[`Cost ${compareYear}`] = parseFloat(cm.supplier_cost.toFixed(2));
      row[`Profit ${compareYear}`] = parseFloat(cm.profit.toFixed(2));
    }
    return row;
  }) : [];

  // Year-over-year deltas (only when compareData present)
  const delta = compareData && data ? {
    revenue: calcDelta(data.summary.total_revenue, compareData.summary.total_revenue),
    cost: calcDelta(data.summary.total_supplier_cost, compareData.summary.total_supplier_cost),
    profit: calcDelta(data.summary.total_profit, compareData.summary.total_profit),
    outstanding: calcDelta(data.summary.outstanding_amount, compareData.summary.outstanding_amount),
  } : null;

  const compareOptions = availableYears.filter(y => y !== selectedYear);

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="accounting-title">Yearly Accounting</h1>
              <p className="text-gray-600 mt-1">Vehicle + Warehouse — Revenue, Cost & Profit by month</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28" data-testid="year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 bg-gray-100 rounded-md px-2 py-1">
                <GitCompareArrows size={16} className="text-gray-600" />
                <Select value={compareYear ? compareYear.toString() : 'none'} onValueChange={(v) => setCompareYear(v === 'none' ? null : parseInt(v))}>
                  <SelectTrigger className="w-36 h-8 border-0 bg-transparent" data-testid="compare-year-select">
                    <SelectValue placeholder="Compare with..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No comparison</SelectItem>
                    {compareOptions.map(y => (
                      <SelectItem key={y} value={y.toString()}>vs {y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-btn">
                <Download size={18} className="mr-2" /> Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {compareData && (
          <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900" data-testid="compare-banner">
            Comparing <strong>{selectedYear}</strong> vs <strong>{compareYear}</strong>. Deltas show change relative to {compareYear}.
          </div>
        )}
        {data && (
          <>
            {/* Summary cards: Revenue / Cost / Profit / Outstanding */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Revenue</p>
                      <p className="text-3xl font-bold text-gray-900" data-testid="total-revenue">{formatCurrency(data.summary.total_revenue)}</p>
                      <p className="text-xs text-gray-500">{data.summary.total_invoices} invoices</p>
                      {compareData && (
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          <span className="text-gray-500">{compareYear}: {formatCurrency(compareData.summary.total_revenue)}</span>
                          <DeltaBadge delta={delta?.revenue} />
                        </div>
                      )}
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <DollarSign className="text-blue-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Supplier Cost</p>
                      <p className="text-3xl font-bold text-red-600" data-testid="total-cost">{formatCurrency(data.summary.total_supplier_cost)}</p>
                      {compareData && (
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          <span className="text-gray-500">{compareYear}: {formatCurrency(compareData.summary.total_supplier_cost)}</span>
                          <DeltaBadge delta={delta?.cost} invertColor />
                        </div>
                      )}
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <DollarSign className="text-red-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Net Profit</p>
                      <p className={`text-3xl font-bold ${data.summary.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="total-profit">
                        {formatCurrency(data.summary.total_profit)}
                      </p>
                      {compareData && (
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          <span className="text-gray-500">{compareYear}: {formatCurrency(compareData.summary.total_profit)}</span>
                          <DeltaBadge delta={delta?.profit} />
                        </div>
                      )}
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Outstanding</p>
                      <p className="text-3xl font-bold text-orange-600" data-testid="outstanding">{formatCurrency(data.summary.outstanding_amount)}</p>
                      <p className="text-xs text-gray-500">{data.summary.unpaid_invoices} unpaid</p>
                      {compareData && (
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          <span className="text-gray-500">{compareYear}: {formatCurrency(compareData.summary.outstanding_amount)}</span>
                          <DeltaBadge delta={delta?.outstanding} invertColor />
                        </div>
                      )}
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="text-orange-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 size={20} /> Monthly Performance — {selectedYear}{compareData ? ` vs ${compareYear}` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="w-full h-80" data-testid="monthly-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip formatter={(v) => `$${(v || 0).toFixed(2)}`} />
                      <Legend />
                      <Bar dataKey={`Revenue ${selectedYear}`} fill="#2563eb" radius={[4, 4, 0, 0]} />
                      <Bar dataKey={`Cost ${selectedYear}`} fill="#dc2626" radius={[4, 4, 0, 0]} />
                      <Bar dataKey={`Profit ${selectedYear}`} fill="#16a34a" radius={[4, 4, 0, 0]} />
                      {compareData && <Bar dataKey={`Revenue ${compareYear}`} fill="#93c5fd" radius={[4, 4, 0, 0]} />}
                      {compareData && <Bar dataKey={`Cost ${compareYear}`} fill="#fca5a5" radius={[4, 4, 0, 0]} />}
                      {compareData && <Bar dataKey={`Profit ${compareYear}`} fill="#86efac" radius={[4, 4, 0, 0]} />}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Table */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar size={20} /> Monthly Breakdown — {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="monthly-table">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-3 font-semibold">Month</th>
                        <th className="text-center py-3 px-3 font-semibold">Inv</th>
                        <th className="text-right py-3 px-3 font-semibold">Vehicle</th>
                        <th className="text-right py-3 px-3 font-semibold">Warehouse</th>
                        <th className="text-right py-3 px-3 font-semibold">Total Revenue</th>
                        <th className="text-right py-3 px-3 font-semibold">Supplier Cost</th>
                        <th className="text-right py-3 px-3 font-semibold">Profit</th>
                        <th className="text-right py-3 px-3 font-semibold">Paid</th>
                        <th className="text-right py-3 px-3 font-semibold">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.monthly_breakdown.map(m => (
                        <tr key={m.month} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium">{m.month_name}</td>
                          <td className="py-2 px-3 text-center">{m.invoice_count}</td>
                          <td className="py-2 px-3 text-right text-gray-700">{formatCurrency(m.vehicle_revenue)}</td>
                          <td className="py-2 px-3 text-right text-gray-700">{formatCurrency(m.warehouse_revenue)}</td>
                          <td className="py-2 px-3 text-right font-semibold text-blue-700">{formatCurrency(m.revenue)}</td>
                          <td className="py-2 px-3 text-right text-red-600">{formatCurrency(m.supplier_cost)}</td>
                          <td className={`py-2 px-3 text-right font-semibold ${m.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(m.profit)}</td>
                          <td className="py-2 px-3 text-right text-green-600">{formatCurrency(m.paid)}</td>
                          <td className="py-2 px-3 text-right text-orange-600">{formatCurrency(m.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-bold border-t-2">
                        <td className="py-3 px-3">TOTAL</td>
                        <td className="py-3 px-3 text-center">{data.summary.total_invoices}</td>
                        <td className="py-3 px-3 text-right">{formatCurrency(data.monthly_breakdown.reduce((s, m) => s + m.vehicle_revenue, 0))}</td>
                        <td className="py-3 px-3 text-right">{formatCurrency(data.monthly_breakdown.reduce((s, m) => s + m.warehouse_revenue, 0))}</td>
                        <td className="py-3 px-3 text-right text-blue-700">{formatCurrency(data.summary.total_revenue)}</td>
                        <td className="py-3 px-3 text-right text-red-600">{formatCurrency(data.summary.total_supplier_cost)}</td>
                        <td className={`py-3 px-3 text-right ${data.summary.total_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(data.summary.total_profit)}</td>
                        <td className="py-3 px-3 text-right text-green-600">{formatCurrency(data.summary.paid_amount)}</td>
                        <td className="py-3 px-3 text-right text-orange-600">{formatCurrency(data.summary.outstanding_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Invoice list */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} /> Invoices — {selectedYear} ({data.invoices.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.invoices.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No invoices found for {selectedYear}</div>
                ) : (
                  <div className="space-y-2 max-h-[480px] overflow-y-auto">
                    {data.invoices.map(inv => {
                      const link = inv.source === 'warehouse'
                        ? `/admin/warehouse/invoice/${inv.id}`
                        : `/admin/billing/invoice/${inv.id}`;
                      return (
                        <div key={inv.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="text-xs uppercase">{inv.source}</Badge>
                            <div>
                              <Link to={link} className="font-semibold text-blue-600 hover:underline">{inv.document_number}</Link>
                              <p className="text-sm text-gray-600">{inv.company_name || inv.customer_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">Rev: <span className="font-semibold text-gray-900">{formatCurrency(inv.total_amount)}</span></span>
                            <span className="text-sm text-gray-600">Cost: <span className="font-semibold text-red-600">{formatCurrency(inv.supplier_cost)}</span></span>
                            <span className="text-sm text-gray-600">Profit: <span className={`font-semibold ${inv.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(inv.profit)}</span></span>
                            <Badge className={inv.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {inv.paid ? 'Paid' : 'Unpaid'}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};
